import './overlays.scss';

import React, { CSSProperties, PropsWithChildren, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { WindowState } from './windowState';
import { LoadMask } from '../loader';
import { useDelayedUnmount } from '../hooks';

export type WindowProps = PropsWithChildren<{
  /** Internal name for identifying the window */
  name?: string;

  /**
   * If true, the window will become automatically visible when the `UIService.activeWindowName` property
   * equals the `name` of the window, only if the `visible` property is not false.
   * @default true
   */
  visibleWhenActive?: boolean;

  title?: string;
  className?: string;
  visible?: boolean;
  isLoading?: boolean;

  /**
   * If true, the window will send a `closeWindow` command to the UIService when the user taps outside of the window area.
   * @default true
   */
  closeOnOutsideClick?: boolean;

  closeOnEscKey?: boolean;
  onExited?: () => void;
  onEntered?: () => void;

  showHeader?: boolean;
  footerButtons?: React.ReactNode;
  innerRef?: React.Ref<HTMLDivElement>;
  style?: CSSProperties;

  model: WindowState;
}>;

export const Window = observer(
  ({ model, onEntered, onExited, innerRef, style, closeOnOutsideClick: _closeOnOutsideClick, ...props }: WindowProps) => {
    const { shouldRender, isAnimating } = useDelayedUnmount(!!props.visible, 400);
    const prevVisibleRef = useRef(props.visible);

    const className = classNames(
      'window modal-window',
      { 'window-loading': props.isLoading },
      props.className
    );

    useEffect(() => {
      model.handleMounted();
      return () => model.handleUnmounted();
    }, [model]);

    // Handle onEntered/onExited callbacks
    useEffect(() => {
      if (props.visible && !prevVisibleRef.current) {
        // Just became visible - trigger onEntered after animation
        const timer = setTimeout(() => onEntered?.(), 400);
        prevVisibleRef.current = true;
        return () => clearTimeout(timer);
      } else if (!props.visible && prevVisibleRef.current) {
        // Just became hidden - trigger onExited after animation
        const timer = setTimeout(() => onExited?.(), 400);
        prevVisibleRef.current = false;
        return () => clearTimeout(timer);
      }
    }, [props.visible, onEntered, onExited]);

    if (!shouldRender) return null;

    return (
      <div data-window-name={props.name}>
        <div className={classNames('window-mask window-animate', isAnimating)} />

        <div
          style={style}
          ref={innerRef}
          className={classNames('window-container window-animate', isAnimating)}>
          <div className="window-outer">
            <div className={className} ref={model.refProxy.ref}>
              {props.children}

              <LoadMask
                visible={props.isLoading}
                className="window-load small"
                spinner="dots5"
                spinnerClassName="medium"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);
